import { EntityTypeEnum, FilterOperation, SubscriptionFilter, TradingEntity } from 'api'
import { Locale, Translation } from 'locale'
import React from 'react'
import { useSelector } from 'react-redux'
import { authorisedUserSelector } from 'selectors/connectionSelectors'
import { createAllEntitiesSortedSelector } from 'selectors/entitySelectors'
import { isBuySideTraderSelector } from 'selectors/permissionSelectors'
import { allSellSideTradingEntityIdsSelector } from 'selectors/tradingEntitySelectors'
import { tradingRelationshipsGetterSelector } from 'selectors/tradingRelationshipSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { ComboboxField } from 'view/components/options'
import {
  getOrganisationIdFromTradingEntityIdsString,
  getSellSideOrBuySideTradingEntityIdsFromRelationships,
  getSubscriptionFilterValue,
  getTradingEntityIdsBelongingToOrganisation,
  getTradingEntityNameById,
  sortTradingEntities
} from '../utils'

interface CounterpartySubscriptionComboboxProps {
  subscriptionFilters: SubscriptionFilter[]
  handleUpdateFilterValue: (fieldName: string, filterOperation: FilterOperation) => (value: any) => void
}

export const CounterpartySubscriptionCombobox: React.FC<CounterpartySubscriptionComboboxProps> = ({
  subscriptionFilters,
  handleUpdateFilterValue
}) => {
  const isAdmin = useSelector(isAdminGatewayUserSelector)
  const isBuySideUser = useSelector(isBuySideTraderSelector)
  const authorisedUser = useSelector(authorisedUserSelector)
  const getTradingRelationships = useSelector(tradingRelationshipsGetterSelector)
  const allSellSideTradingEntityIds = useSelector(allSellSideTradingEntityIdsSelector)
  const allTradingEntities: TradingEntity[] = useSelector(createAllEntitiesSortedSelector(EntityTypeEnum.TRADING_ENTITY))

  const counterpartyFirmSubscriptionFilterValue =
    isAdmin || isBuySideUser
      ? getSubscriptionFilterValue(subscriptionFilters, 'counterpartyId', FilterOperation.CONTAINS)
      : getSubscriptionFilterValue(subscriptionFilters, 'initiatorOrganisationId')
  const tradingRelationships = authorisedUser.tradingEntityDetails.map(({ tradingEntityId }) => {
    return getTradingRelationships(tradingEntityId)
  })
  const filteredTradingEntityIds =
    isAdmin || isBuySideUser
      ? getTradingEntityIdsBelongingToOrganisation(
          allTradingEntities,
          getOrganisationIdFromTradingEntityIdsString(allTradingEntities, counterpartyFirmSubscriptionFilterValue)
        )
      : getTradingEntityIdsBelongingToOrganisation(allTradingEntities, counterpartyFirmSubscriptionFilterValue)
  const nonFilteredTradingEntityIds = isAdmin
    ? allSellSideTradingEntityIds
    : getSellSideOrBuySideTradingEntityIdsFromRelationships(tradingRelationships, isBuySideUser)
  const tradingEntityIds = counterpartyFirmSubscriptionFilterValue ? filteredTradingEntityIds : nonFilteredTradingEntityIds

  return (
    <ComboboxField
      label={Locale.translate(Translation.SC_FILTER_COUNTERPARTY_EQUALS)}
      dataTestId={'AdvancedDealSubscriptionFilters-Selectbox-Counterparty'}
      canClear={true}
      optionKeys={sortTradingEntities(tradingEntityIds, allTradingEntities)}
      getOption={getTradingEntityNameById(allTradingEntities)}
      value={
        isBuySideUser || isAdmin
          ? getSubscriptionFilterValue(subscriptionFilters, 'counterpartyId', FilterOperation.EQUALS)
          : getSubscriptionFilterValue(subscriptionFilters, 'fundId', FilterOperation.EQUALS)
      }
      onUpdate={handleUpdateFilterValue(isBuySideUser || isAdmin ? 'counterpartyId' : 'fundId', FilterOperation.EQUALS)}
    />
  )
}
